import { config } from '@/config';

/**
 * The health endpoints (04 §4.11) live OUTSIDE the `/api/v1` prefix and outside
 * the auth/rate-limit chain, so they do not go through `lib/api.ts`: no bearer
 * token, no error envelope, no 401 refresh dance. A public caller sees only
 * `200`/`503` — component details need the `X-Health-Token` header (07 §9.2),
 * which the browser never has.
 */

export type HealthState = 'up' | 'down';

export interface HealthReadyResult {
  ok: boolean;
  status: number;
  /** `'ok' | 'error' | 'shutting_down'` from terminus, when the body carries it. */
  reported?: string;
  /** Per-component states, present only for a privileged caller. */
  components?: Record<string, HealthState>;
}

export interface HealthVersionResult {
  version?: string;
  commit?: string;
  builtAt?: string;
  node?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

async function readJsonSafely(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return undefined;
  }
}

/**
 * `GET /health/ready` — real Postgres + Redis checks on the API side.
 * A 503 is a legitimate answer (the API is up but a dependency is not), so this
 * resolves rather than throwing; only a transport failure rejects.
 */
export async function fetchHealthReady(signal?: AbortSignal): Promise<HealthReadyResult> {
  const response = await fetch(`${config.healthUrl}/ready`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });

  const body = await readJsonSafely(response);
  const result: HealthReadyResult = { ok: response.ok, status: response.status };

  if (isRecord(body)) {
    if (typeof body.status === 'string') result.reported = body.status;
    const details = isRecord(body.info) ? body.info : isRecord(body.details) ? body.details : null;
    if (details) {
      const components: Record<string, HealthState> = {};
      for (const [name, value] of Object.entries(details)) {
        const state = isRecord(value) && value.status === 'up' ? 'up' : 'down';
        components[name] = state;
      }
      result.components = components;
    }
  }

  return result;
}

/** `GET /health/version` — shown in the footer next to the build SHA. */
export async function fetchHealthVersion(signal?: AbortSignal): Promise<HealthVersionResult> {
  const response = await fetch(`${config.healthUrl}/version`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) return {};
  const body = await readJsonSafely(response);
  return isRecord(body) ? (body as HealthVersionResult) : {};
}
